import React, { useState, PropsWithChildren, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import If from '@components/If';
import useIsAuthenticated from '@core/hooks/useIsAuthenticated';
import useOrganizationSettings from '@core/hooks/useOrganizationSettings';
import { GetStudyResponse } from '@core/types';
import { hideStartLoader } from '@core/utils/startLoader';
import useGetStudyTokenWithoutOrgSecurity from '../../hooks/useGetStudyTokenWithoutOrgSecurity';
import BirthdateForm from './BirthdateForm';
import 'dayjs/locale/es';

const StudyConfirmAuthWrapper = ({
  children,
  studyInfo,
}: PropsWithChildren<{ studyInfo?: GetStudyResponse }>) => {
  // Both hooks below must be pinned to studyInfo.organizationId explicitly
  // (the same value ThemeProvider uses) rather than letting them fall back
  // to AuthContext/sessionStorage's own organizationId: for an anonymous
  // viewer that fallback chain isn't populated yet on first render, so
  // useGetStudyTokenWithoutOrgSecurity was sometimes writing its token into
  // a [DICOM_SERVICE_SETTINGS_QUERY, undefined] cache entry instead of the
  // one keyed by the real org id that Viewer/ThemeContext actually reads —
  // permanently starving the viewer of a token with no error shown.
  const { organizationSettings, isLoading } = useOrganizationSettings(studyInfo?.organizationId);
  const [isCorrectDate, setIsCorrectDate] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const [searchParams] = useSearchParams();
  const studyHash = searchParams.get('s');

  const isSecurityStudyActivated = organizationSettings.StudySecurity === 'True';

  // Inside logic: this is updating query key token
  useGetStudyTokenWithoutOrgSecurity(
    studyHash!,
    !isSecurityStudyActivated && !!studyHash,
    studyInfo?.organizationId,
  );

  useEffect(() => {
    if (isSecurityStudyActivated && !isLoading) hideStartLoader({ isAuthenticated: true });
  }, [organizationSettings]);

  return (
    <If
      fallback={<BirthdateForm studyInfo={studyInfo} setIsCorrectDate={setIsCorrectDate} />}
      condition={isCorrectDate || isAuthenticated || !isSecurityStudyActivated}
    >
      {children}
    </If>
  );
};

export default StudyConfirmAuthWrapper;
